import styled from "styled-components";
import { useEffect, useState } from "react";

import {
  BRICK_TERRACOTA_800,
  DAVYS_GREY_300,
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  MOSS_GREEN_800,
  PLUM_PURPLE_700,
  white,
} from "../common/colors";
import FlexBox from "../common/FlexBox";
import { Body1, Body2, H2 } from "../common/Headings";
import aiIcon from "../../assets/ai-icon.svg";
import { Modal } from "../common/Modal";
import { Button } from "../common/Buttons";
import axiosInstance from "../../axiosInstance";
import { trackEvent } from "../../helperFunctions";
import Bugsnag from "@bugsnag/js";
import { useDispatch } from "react-redux";
import { addToast } from "../../Store/Actions";
import URL from "../../urls";
import aiHeader from "../../assets/ai-modal-step-1.svg";
import { CloseIcon, Header } from "../common/AIConsentModal/styles";
import { FiX } from "react-icons/fi";

const Card = styled(FlexBox)`
  flex-direction: column;
  border: 1px solid ${DAVYS_GREY_300};
  border-radius: 0.875rem;
  background: ${white};
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${DAVYS_GREY_300};
`;

const TranscriptionStatusText = styled(Body2)`
  color: ${BRICK_TERRACOTA_800};
  font-weight: 700;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ToggleSwitch = styled.input`
  appearance: none;
  -webkit-appearance: none;
  width: 2.5rem;
  height: 1.3rem;
  border-radius: 999px;
  background: ${props => (props.checked ? MOSS_GREEN_800 : DAVYS_GREY_400)};
  cursor: pointer;
  position: relative;
  transition: background 0.3s;

  &::before {
    content: "";
    position: absolute;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${white};
    top: 0.15rem;
    left: ${props => (props.checked ? "1.25rem" : "0.15rem")};
    transition: left 0.3s;
  }
`;

const AIIcon = styled.img`
  height: 24px;
  width: 24px;
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(90vh - 9.25rem);
  min-height: 0;
  width: 100%;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 1.5rem 2.5rem;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalFooter = styled.div`
  border-top: 1px solid ${DAVYS_GREY_300};
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const SessionTranscriptionModal = ({
  isOpen,
  onClose,
  isEnabled,
  setIsEnabled,
}) => {
  const dispatch = useDispatch();
  const [isTranscriptionEnabled, setIsTranscriptionEnabled] =
    useState(isEnabled);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsTranscriptionEnabled(isEnabled);
  }, [isEnabled, isOpen]);

  if (!isOpen) return null;

  const handleToggle = () => {
    setIsTranscriptionEnabled(!isTranscriptionEnabled);
  };

  const saveConsent = async () => {
    if (isSaving) return;
    if (isTranscriptionEnabled === isEnabled) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      await axiosInstance.post(URL.aiPermission, {
        permission: isTranscriptionEnabled,
      });
      trackEvent({
        event: isTranscriptionEnabled ? "twa_ai_enabled" : "twa_ai_disabled",
        payload: { source: "ai_home" },
      });
      setIsEnabled(isTranscriptionEnabled);
      dispatch(
        addToast({
          msg: `Transcription ${isTranscriptionEnabled ? "enabled" : "disabled"} successfully.`,
          autoDismiss: true,
          appearance: "success",
          showBottomToastOnMobile: true,
        })
      );
    } catch (err) {
      Bugsnag.notify(err);
      dispatch(
        addToast({
          msg: "Failed to update transcription preference. Please try again.",
          autoDismiss: true,
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  return (
    <Modal
      M1
      height="90vh"
      togglePopup={onClose}
      backgroundClickEnabled
      borderRadius="1rem"
      mobileFullScreen
      mobileHeight={"100vh"}
    >
      <Header bgImage={aiHeader} headerHeight="9.25rem">
        <CloseIcon onClick={onClose}>
          <FiX />
        </CloseIcon>
      </Header>
      <ModalWrapper>
        <ModalBody>
          <FlexBox column rowGap="1rem">
            <FlexBox column rowGap="0.5rem">
              <H2 color={BRICK_TERRACOTA_800} bold textAlign="center">
                Session Transcription
              </H2>

              <Body1 textAlign="center">
                To help you deliver the best care, we’re excited to introduce
                session transcription and summarisation. This feature allows you
                to capture detailed insights from each session, empowering you
                to better understand, track, and support the progress of those
                you work with.
              </Body1>
            </FlexBox>
            <Card>
              <FlexBox column rowGap="0.75rem" padding="1rem">
                <Body2 color={DAVYS_GREY_700}>
                  By turning on this feature, you gain a powerful tool to
                  enhance the process, helping you better understand and support
                  the progress of those you work with.
                </Body2>
              </FlexBox>
              <Divider />
              <FlexBox align="center" justify="space-between" padding="1rem">
                <FlexBox align="center" gap="0.75rem">
                  <AIIcon src={aiIcon} />
                  <Body1 color={PLUM_PURPLE_700} bold>
                    Amaha AI
                  </Body1>
                </FlexBox>
                <FlexBox alignItems="center" gap="0.75rem">
                  <TranscriptionStatusText>
                    {isTranscriptionEnabled
                      ? "Transcription Enabled"
                      : "Transcription Disabled"}
                  </TranscriptionStatusText>
                  <ToggleSwitch
                    type="checkbox"
                    checked={isTranscriptionEnabled}
                    onChange={handleToggle}
                  />
                </FlexBox>
              </FlexBox>
            </Card>
          </FlexBox>
        </ModalBody>
      </ModalWrapper>
      <ModalFooter>
        <Button onClick={saveConsent} disabled={isSaving}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SessionTranscriptionModal;
